// Copyright ©️ 2026 eVolve MEP, LLC

import './forge-viewer.css';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import {
  extractErrorMessage,
  type AttachmentFile,
  type EntityAttachment,
  type FileId,
  type UpdateMarkupBody,
  useEvolvePost,
} from '@evolvemep/api-ts';
import { Box, Flex, Loader, Text } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';

import { DeferredForgeViewer } from 'components/Autodesk/DeferredForgeViewer';
import type { MarkupModeExtension } from 'components/Autodesk/extensions/MarkupModeExtension/MarkupModeExtension';
import { useViewer } from 'components/Autodesk/hooks/useViewer';
import { useEvolveExtensions } from 'components/Autodesk/useEvolveExtensions';
import {
  FILE_PREVIEW_SUPPORTED_CONTENT_TYPES,
  type FilePreviewSupportedContentType,
} from 'components/Mantine/FileUploads/fileContentTypes';
import { useFileCache } from 'components/Mantine/FileUploads/useFileCache';
import { NoRowsOverlay } from 'helpers/ag-grid/NoRowsOverlay';
import { isNotNil, isNil } from 'helpers/isNotNil';

import type { PageNavExtension } from './extensions/PageNavExtension';
import { useHideExtensions } from './extensions/useHideExtensions';

const { Autodesk } = window;

type PdfImageViewerProps = {
  /** Pre-signed URL for the selected attachment file content. */
  preSignedUrl: string;
  /**
   * MIME type of the content to load, used to determine how to render the content.
   * Images get rendered as PDFs in the viewer.
   * */
  contentType: FilePreviewSupportedContentType;
  /** File to view, including markup. */
  file: AttachmentFile | null;
};

/** Internal component that renders PDFs and images in the Autodesk viewer. */
const PdfImageViewer = ({ preSignedUrl, contentType, file }: PdfImageViewerProps) => {
  const { mounted, viewer, viewerRuntimePromise } = useViewer();
  const pageNavExtRef = useRef<PageNavExtension | null>(null);
  const markupExtRef = useRef<MarkupModeExtension | null>(null);
  const hideExtensions = useHideExtensions(['measurementSubmenu']);

  const { downloadFile, updateCache } = useFileCache();
  const { contexts, loadEvolveExtension } = useEvolveExtensions();
  const { apiCall: updateMarkupApiCall } = useEvolvePost('shop/file/:fileId/markup');
  const updateMarkup = useCallback(
    async (fileIdToUpdate: FileId, body: UpdateMarkupBody) => {
      const response = await updateMarkupApiCall(body, { url: `shop/file/${fileIdToUpdate}/markup` });
      updateCache(fileIdToUpdate, { fileMarkup: response });
    },
    [updateMarkupApiCall, updateCache],
  );

  const initializeViewerRuntime = useCallback(async () => {
    viewerRuntimePromise.current ??= new Promise<void>((resolve) =>
      Autodesk.Viewing.Initializer({ env: 'Local', shouldInitializeAuth: false }, resolve),
    );
    return viewerRuntimePromise.current;
    // oxlint-disable-next-line react/preserve-manual-memoization
  }, [viewerRuntimePromise]);

  const loadContent = useCallback(
    async (page = 1) => {
      if (isNil(viewer.current) || isNil(viewer.current.impl)) return;
      const totalPages = pageNavExtRef.current?.totalPages ?? 0;
      const isInvalidPage = page !== 1 && (page <= 0 || page > totalPages || !Number.isInteger(page));
      if (isInvalidPage) return;

      if (isNotNil(viewer.current.impl.model)) {
        viewer.current.unloadModel(viewer.current.impl.model);
      }

      if (contentType === 'application/pdf') {
        const prevPage = pageNavExtRef.current?.currentPage ?? 1;
        viewer.current.loadModel(preSignedUrl, { page }, async (model) => {
          const newTotalPages =
            // @ts-expect-error Autodesk's PDFLoader is not typed properly
            // oxlint-disable-next-line typescript/no-unsafe-member-access
            model.loader.pdf?.numPages ?? 0;
          if (page !== prevPage) {
            markupExtRef.current?.notifyPageChange(page);
          }
          pageNavExtRef.current?.setPageInfo(page, newTotalPages);
        });
        return;
      }

      if (contentType.startsWith('image/')) {
        const blob = await fetch(preSignedUrl).then((res) => res.blob());

        const img = new Image();
        const imageUrl = URL.createObjectURL(blob);
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imageUrl;
        }).catch((e) => {
          notifications.show({
            title: 'Failed to load image',
            message: extractErrorMessage(e),
            color: 'red',
          });
          throw e;
        });

        const { jsPDF } = await import('jspdf');

        const aspectRatio = img.width / img.height;
        const pdf = new jsPDF({
          orientation: aspectRatio > 1 ? 'landscape' : 'portrait',
          unit: 'pt',
          format: [img.width, img.height],
        });

        const canvas = new OffscreenCanvas(img.width, img.height);
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);
        const pngDataUrl = URL.createObjectURL(await canvas.convertToBlob({ type: 'image/png', quality: 1 }));

        pdf.addImage(pngDataUrl, 'PNG', 0, 0, img.width, img.height, undefined, 'NONE');

        const pdfArrayBuffer = pdf.output('arraybuffer');
        const pdfBlob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(pdfBlob);

        viewer.current.loadModel(`${blobUrl}#.pdf`, { page: 1 }, async () => {
          URL.revokeObjectURL(blobUrl);
          URL.revokeObjectURL(imageUrl);
          img.remove();
        });
        return;
      }

      notifications.show({
        title: `Unsupported file type: ${contentType}`,
        message: 'Something has gone wrong. Please contact support.',
        color: 'red',
      });
    },
    // oxlint-disable-next-line react/preserve-manual-memoization
    [contentType, preSignedUrl, viewer],
  );

  // Stable ref so the extension always invokes the latest version of loadContent,
  // even if it was recreated due to prop changes within the same mount lifecycle.
  const loadContentRef = useRef(loadContent);
  loadContentRef.current = loadContent;

  const resize = useDebouncedCallback(() => viewer.current?.resize(), 250);

  return (
    <div
      ref={(ref: HTMLDivElement | null) => {
        if (!ref) return undefined;

        initializeViewerRuntime()
          .then(async () => {
            if (viewer.current || !mounted.current) return;

            viewer.current = new Autodesk.Viewing.GuiViewer3D(ref);

            await Promise.allSettled([
              // `enableBrowserNavigation` allows the extension to push entries to the browser history
              // with a `page` param every time the user navigates to another PDF page, which we don't want.
              viewer.current.loadExtension('Autodesk.PDF', { enableBrowserNavigation: false }),
              loadEvolveExtension(viewer.current, 'MarkupModeExtension', {
                file,
                updateMarkup,
                downloadFile,
                viewerMarkupStore: contexts['viewerMarkup'],
              }),
              loadEvolveExtension(viewer.current, 'PageNavExtension', {
                onLoadPage: (page: number) => loadContentRef.current(page),
              }),
            ]);

            markupExtRef.current = viewer.current.getExtension('MarkupModeExtension') as MarkupModeExtension | null;
            pageNavExtRef.current = viewer.current.getExtension('PageNavExtension') as PageNavExtension | null;

            viewer.current.addEventListener(Autodesk.Viewing.EXTENSION_LOADED_EVENT, hideExtensions);

            await viewer.current.start();

            await loadContent();
          })
          // oxlint-disable-next-line no-console
          .catch(console.error);

        const resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(ref);
        return () => {
          resizeObserver.disconnect();
        };
      }}
    />
  );
};

/** Wrapper that delays rendering to allow previous viewer to unmount. */
const PdfImageViewerDelayed = (props: PdfImageViewerProps) => (
  <DeferredForgeViewer url={props.preSignedUrl}>
    {(deferredUrl) => <PdfImageViewer {...props} preSignedUrl={deferredUrl} />}
  </DeferredForgeViewer>
);

/** Used to render an Attachment (PDF, JPEG, or PNG). */
export const ForgeViewerFromAttachment = ({
  attachment,
  headerContents,
  noDocumentSelected = 'Select a file.',
  noHeader = false,
}: {
  attachment?: EntityAttachment<never> | null;
  headerContents?: ReactNode;
  noHeader?: boolean;
  noDocumentSelected?: ReactNode;
}) => {
  const isFileAttachment = isNotNil(attachment) && 'file' in attachment;
  const fileId = isFileAttachment ? attachment.file.fileId : undefined;

  const { presigningInProgress, getPresignedUrl, presignedUrlCache, downloadFile } = useFileCache();
  const [fileContentType, setFileContentType] = useState<FilePreviewSupportedContentType | null>(null);
  const cachedFileId = useRef<FileId>(undefined);
  const [signedURL, setSignedUrl] = useState<string>();

  const presignedAttachment = (isNotNil(fileId) ? presignedUrlCache[fileId] : undefined)?.response;

  // Prefer the file cache's copy of the markup when the file is cached: after a markup edit the
  // cache holds the latest markup, whereas the attachment from the list may still be stale.
  const file: AttachmentFile | null = isFileAttachment
    ? {
        ...attachment.file,
        ...presignedAttachment,
      }
    : null;

  useEffect(() => {
    if (fileId !== cachedFileId.current) {
      cachedFileId.current = undefined;
      setSignedUrl(undefined);
      setFileContentType(null);
      if (fileId) {
        void getPresignedUrl(fileId);
      }
    }
  }, [fileId, getPresignedUrl]);

  useEffect(() => {
    const supportedContentType = FILE_PREVIEW_SUPPORTED_CONTENT_TYPES.find(
      (ct) => presignedAttachment?.contentType.toLowerCase() === ct,
    );
    if (presignedAttachment?.fileId && supportedContentType) {
      cachedFileId.current = presignedAttachment.fileId;
      setSignedUrl(presignedAttachment.preSignedUrl);
      setFileContentType(supportedContentType);
    }
  }, [presignedAttachment]);

  const isSupported = isNotNil(fileContentType);

  const downloadAttachment = () => isFileAttachment && downloadFile(attachment.file.fileId);

  return (
    <Box
      display="flex"
      style={{
        width: '100%',
        height: '100%',
        flexDirection: 'column',
      }}
    >
      {!noHeader && (
        <Flex px="sm" align="center" className="forge-header" gap="md">
          {headerContents}
          <Text fw={500} lineClamp={1}>
            {isFileAttachment ? attachment.file.fileName : attachment?.webLink.name || attachment?.webLink.url}
          </Text>
        </Flex>
      )}
      {isNotNil(attachment) ? (
        <>
          {isSupported || presigningInProgress ? (
            <>
              {isNotNil(signedURL) && isNotNil(fileContentType) ? (
                <Box pos="relative" className="forge-container" flex={1}>
                  <PdfImageViewerDelayed preSignedUrl={signedURL} contentType={fileContentType} file={file} />
                </Box>
              ) : (
                <Flex align="center" justify="center" style={{ height: '100%' }}>
                  <Loader />
                </Flex>
              )}
            </>
          ) : (
            <Flex align="center" justify="center" style={{ height: '100%' }}>
              <NoRowsOverlay
                label={
                  <Flex ta="center" direction="column" gap={4}>
                    <Text>File type unsupported.</Text>
                    <Text>
                      Click{' '}
                      <Text fw={500} span className="attachment-link" onClick={downloadAttachment}>
                        here
                      </Text>{' '}
                      to download.
                    </Text>
                  </Flex>
                }
                icon="ModelBrowser"
              />
            </Flex>
          )}
        </>
      ) : (
        <Flex align="center" justify="center" style={{ height: '100%' }}>
          <NoRowsOverlay label={noDocumentSelected} icon="ModelBrowser" />
        </Flex>
      )}
    </Box>
  );
};
